import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DashboardLayout } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import TableWithSearch from "@/components/TableWithSearch";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { providerManagerTableHeaders } from "@/utils/tablesData";
import { KeyRound, Lock, Plus, ToggleRight } from "lucide-react";
import { toast } from "sonner";
import AxiosInstance from "@/utils/AxiosInstance";
import { getPromise, getPromiseToast } from "@/utils/helpers";
import { permissionFlagTruthy } from "@/contexts/use-auth";
import RHFWrapper from "@/components/RHFWrapper";
import RhfInput from "@/components/rhfFields/RhfInput";
import { ResetSchema } from "@/pages/ResetPassword";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PERMISSIONS_CATALOG_URL = "/providers/me/managers/permissions-catalog";

type PermissionCatalogItem = { key: string; label: string };

export type ProviderAccountStatus = "active" | "suspended" | "inactive";

const PROVIDER_ACCOUNT_STATUS_OPTIONS: {
  value: ProviderAccountStatus;
  label: string;
}[] = [
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
];

export type ProviderManager = {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  status: ProviderAccountStatus;
  enabled: boolean;
  permissions: Record<string, boolean>;
};

function normalizeAccountStatus(raw: unknown): ProviderAccountStatus {
  const s = String(raw ?? "").trim().toLowerCase();
  if (s === "active") return "active";
  if (s === "inactive") return "inactive";
  return "suspended";
}

function statusFromEnabled(enabled: boolean): ProviderAccountStatus {
  return enabled ? "active" : "inactive";
}

function formatPermissionKeyLabel(key: string): string {
  const spaced = key
    .replace(/^canAccess/, "")
    .replace(/([A-Z])/g, " $1")
    .trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : key;
}

function catalogItemFromEntry(
  keyRaw: string,
  value: unknown,
): PermissionCatalogItem | null {
  const key = keyRaw.trim();
  if (!key.startsWith("canAccess")) return null;

  if (typeof value === "string" && value.trim()) {
    return { key, label: value.trim() };
  }
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const nested = value as Record<string, unknown>;
    const nestedKey = String(nested.key ?? nested.id ?? key).trim();
    const label = String(nested.label ?? nested.name ?? nested.title ?? "").trim();
    return {
      key: nestedKey || key,
      label: label || formatPermissionKeyLabel(nestedKey || key),
    };
  }
  return { key, label: formatPermissionKeyLabel(key) };
}

function catalogFromPermissionMap(obj: Record<string, unknown>): PermissionCatalogItem[] {
  const items: PermissionCatalogItem[] = [];
  for (const [key, value] of Object.entries(obj)) {
    const item = catalogItemFromEntry(key, value);
    if (item) items.push(item);
  }
  return items;
}

function catalogFromArray(list: unknown[]): PermissionCatalogItem[] {
  const items: PermissionCatalogItem[] = [];
  for (const row of list) {
    if (typeof row === "string") {
      const item = catalogItemFromEntry(row, row);
      if (item) items.push(item);
      continue;
    }
    if (typeof row !== "object" || row === null) continue;
    const o = row as Record<string, unknown>;
    const key = String(o.key ?? o.id ?? o.permission ?? "").trim();
    if (!key) continue;
    const label = String(o.label ?? o.name ?? o.title ?? "").trim();
    items.push({
      key,
      label: label || formatPermissionKeyLabel(key),
    });
  }
  return items;
}

/** Parse `GET /providers/me/managers/permissions-catalog` — API-only, no static fallback. */
function parsePermissionsCatalog(body: unknown): PermissionCatalogItem[] {
  if (!body || typeof body !== "object") return [];

  const seen = new Set<string>();
  const merged: PermissionCatalogItem[] = [];
  const add = (items: PermissionCatalogItem[]) => {
    for (const item of items) {
      if (!item.key || seen.has(item.key)) continue;
      seen.add(item.key);
      merged.push(item);
    }
  };

  const root = body as Record<string, unknown>;
  const layers: unknown[] = [
    root,
    root.data,
    (root.data as Record<string, unknown> | undefined)?.data,
    (root.data as Record<string, unknown> | undefined)?.permissions,
    (root.data as Record<string, unknown> | undefined)?.catalog,
    root.permissions,
    root.catalog,
  ];

  for (const layer of layers) {
    if (Array.isArray(layer)) {
      add(catalogFromArray(layer));
      continue;
    }
    if (layer && typeof layer === "object") {
      const block = layer as Record<string, unknown>;
      if (Array.isArray(block.permissions)) add(catalogFromArray(block.permissions));
      if (Array.isArray(block.catalog)) add(catalogFromArray(block.catalog));
      if (Array.isArray(block.items)) add(catalogFromArray(block.items));
      add(catalogFromPermissionMap(block));
    }
  }

  return merged;
}

function extractManagersList(body: unknown): unknown[] {
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const data = root.data ?? root;
  if (Array.isArray(data)) return data;
  if (typeof data === "object" && data !== null) {
    const block = data as Record<string, unknown>;
    if (Array.isArray(block.managers)) return block.managers;
    if (Array.isArray(block.users)) return block.users;
    if (Array.isArray(block.data)) return block.data;
  }
  return [];
}

function mapApiManager(raw: unknown): ProviderManager {
  const o =
    typeof raw === "object" && raw !== null ? (raw as Record<string, unknown>) : {};
  const id = o._id ?? o.id;
  const firstName = String(o.firstName ?? "").trim();
  const lastName = String(o.lastName ?? "").trim();
  const perms =
    typeof o.permissions === "object" && o.permissions !== null
      ? (o.permissions as Record<string, unknown>)
      : {};
  const permissions: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(perms)) {
    permissions[k] = permissionFlagTruthy(v);
  }
  const statusRaw = o.status ?? o.accessStatus;
  const status =
    statusRaw != null && statusRaw !== ""
      ? normalizeAccountStatus(statusRaw)
      : o.enabled === false || o.isActive === false
        ? "suspended"
        : "active";
  return {
    id: id != null ? String(id) : crypto.randomUUID(),
    firstName,
    lastName,
    name:
      [firstName, lastName].filter(Boolean).join(" ").trim() ||
      String(o.name ?? "").trim(),
    email: String(o.email ?? "").trim(),
    status,
    enabled: status === "active",
    permissions,
  };
}

function emptyDraft(keys: string[]): Record<string, boolean> {
  return Object.fromEntries(keys.map((k) => [k, false]));
}

function draftFromPermissions(
  source: Record<string, boolean>,
  keys: string[],
): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const key of keys) {
    out[key] = !!source[key];
  }
  return out;
}

function AccessNavPermissionFields({
  items,
  draft,
  onToggle,
  idPrefix,
}: {
  items: PermissionCatalogItem[];
  draft: Record<string, boolean>;
  onToggle: (key: string, checked: boolean) => void;
  idPrefix: string;
}) {
  return (
    <div
      className="rounded-lg border border-border bg-muted/25 p-1 max-h-[min(280px,42vh)] overflow-y-auto"
      role="group"
      aria-label="Navigation access"
    >
      {items.map(({ key, label }) => (
        <div
          key={key}
          className="flex items-start gap-3 rounded-md px-3 py-2.5 hover:bg-muted/70 transition-colors"
        >
          <Checkbox
            id={`${idPrefix}-${key}`}
            checked={!!draft[key]}
            onCheckedChange={(v) => onToggle(key, v === true)}
            className="mt-0.5"
          />
          <label
            htmlFor={`${idPrefix}-${key}`}
            className="text-sm leading-snug cursor-pointer text-foreground"
          >
            {label}
          </label>
        </div>
      ))}
    </div>
  );
}

function toTableRow(m: ProviderManager) {
  return {
    _id: m.id,
    firstName: m.name,
    email: m.email,
    __manager: m,
  };
}

export default function ProviderManagerManagement() {
  const [managers, setManagers] = useState<ProviderManager[]>([]);
  const [catalog, setCatalog] = useState<PermissionCatalogItem[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [listVersion, setListVersion] = useState(0);
  const [filters, setFilters] = useState<Record<string, unknown>>({});

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [accountStatus, setAccountStatus] =
    useState<ProviderAccountStatus>("active");
  const [createPermDraft, setCreatePermDraft] = useState<Record<string, boolean>>(
    {},
  );

  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [permTarget, setPermTarget] = useState<ProviderManager | null>(null);
  const [permOnlyDraft, setPermOnlyDraft] = useState<Record<string, boolean>>({});
  const [permSaving, setPermSaving] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState<ProviderManager | null>(null);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const permDraftSyncedForUserIdRef = useRef<string | null>(null);
  const permTargetRef = useRef<ProviderManager | null>(null);
  permTargetRef.current = permTarget;

  const catalogKeys = useMemo(() => catalog.map((c) => c.key), [catalog]);

  const loadPermissionsCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const { data } = await AxiosInstance().get<unknown>(PERMISSIONS_CATALOG_URL);
      const items = parsePermissionsCatalog(data);
      if (!items.length) {
        toast.error("Permissions catalog is empty or could not be read.");
        setCatalog([]);
        setCreatePermDraft({});
        return;
      }
      setCatalog(items);
      setCreatePermDraft((prev) => {
        const next = emptyDraft(items.map((i) => i.key));
        for (const key of items.map((i) => i.key)) {
          if (prev[key] !== undefined) next[key] = prev[key];
        }
        return next;
      });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(
        err?.response?.data?.message ??
          "Could not load permissions catalog from the server.",
      );
      setCatalog([]);
      setCreatePermDraft({});
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPermissionsCatalog();
  }, [loadPermissionsCatalog]);

  useEffect(() => {
    if ((dialogOpen || permDialogOpen) && !catalogLoading && catalog.length === 0) {
      void loadPermissionsCatalog();
    }
  }, [dialogOpen, permDialogOpen, catalogLoading, catalog.length, loadPermissionsCatalog]);

  useEffect(() => {
    if (!permDialogOpen) {
      permDraftSyncedForUserIdRef.current = null;
      return;
    }
    const manager = permTargetRef.current;
    if (!manager) return;
    const mid = manager.id;
    setPermOnlyDraft((prev) => {
      if (permDraftSyncedForUserIdRef.current !== mid) {
        permDraftSyncedForUserIdRef.current = mid;
        return draftFromPermissions(manager.permissions, catalogKeys);
      }
      const next: Record<string, boolean> = {};
      for (const k of catalogKeys) {
        next[k] = prev[k] !== undefined ? !!prev[k] : !!manager.permissions[k];
      }
      return next;
    });
  }, [permDialogOpen, permTarget?.id, catalogKeys]);

  const isEdit = editingId !== null;

  const handleManagersFromApi = useCallback((raw: unknown) => {
    const list = Array.isArray(raw)
      ? raw
      : extractManagersList(
          raw && typeof raw === "object" && raw !== null && "data" in raw
            ? raw
            : { data: raw },
        );
    setManagers(list.map(mapApiManager));
  }, []);

  const tableRows = useMemo(() => managers.map((m) => toTableRow(m)), [managers]);

  const toggleManagerEnabled = useCallback(
    async (manager: ProviderManager, enabled: boolean) => {
      const previous = manager.enabled !== false;
      const nextStatus = statusFromEnabled(enabled);
      setManagers((prev) =>
        prev.map((m) =>
          m.id === manager.id
            ? { ...m, enabled, status: nextStatus }
            : m,
        ),
      );
      try {
        await AxiosInstance().put(`/providers/me/managers/${manager.id}`, {
          firstName: manager.firstName,
          lastName: manager.lastName,
          email: manager.email,
          status: nextStatus,
        });
        toast.success(enabled ? "Manager enabled." : "Manager disabled.");
      } catch (e: unknown) {
        setManagers((prev) =>
          prev.map((m) =>
            m.id === manager.id ? { ...m, enabled: previous } : m,
          ),
        );
        const err = e as { response?: { data?: { message?: string } } };
        toast.error(
          err?.response?.data?.message ?? "Failed to update manager status",
        );
      }
    },
    [],
  );

  const rowToManager = (row: any): ProviderManager | undefined => row?.__manager;

  const resetForm = () => {
    setEditingId(null);
    setFirstName("");
    setLastName("");
    setEmail("");
    setAccountStatus("active");
    setCreatePermDraft(emptyDraft(catalogKeys));
  };

  const openAdd = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (m: ProviderManager) => {
    setEditingId(m.id);
    setFirstName(m.firstName);
    setLastName(m.lastName);
    setEmail(m.email);
    setAccountStatus(m.status);
    setDialogOpen(true);
  };

  const openPermissionsOnly = (m: ProviderManager) => {
    setPermTarget(m);
    setPermDialogOpen(true);
  };

  const openChangePassword = (m: ProviderManager) => {
    setPasswordTarget(m);
    setPasswordDialogOpen(true);
  };

  const handleProviderResetPassword = async (formData: { password: string }) => {
    if (!passwordTarget) return;
    const { pr, resolveFn, rejectFn } = getPromise();
    getPromiseToast(pr);
    try {
      setPasswordSaving(true);
      await AxiosInstance().post("/auth/provider/reset-manager-password", {
        userId: passwordTarget.id,
        password: formData.password,
      });
      resolveFn("Password updated.");
      setPasswordDialogOpen(false);
      setPasswordTarget(null);
    } catch (error) {
      rejectFn(error);
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) resetForm();
  };

  const buildPermissionsPayload = (draft: Record<string, boolean>) => {
    const permissions: Record<string, boolean> = {};
    for (const key of catalogKeys) {
      permissions[key] = !!draft[key];
    }
    return { permissions };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedFirst) {
      toast.error("Please enter a first name.");
      return;
    }
    if (!trimmedEmail || !EMAIL_RE.test(trimmedEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    const emailTaken = managers.some(
      (m) => m.email.toLowerCase() === trimmedEmail && m.id !== editingId,
    );
    if (emailTaken) {
      toast.error("Another manager already uses this email.");
      return;
    }

    const editingManager = isEdit
      ? managers.find((m) => m.id === editingId)
      : undefined;

    const { pr, resolveFn, rejectFn } = getPromise();
    getPromiseToast(pr);
    try {
      setSubmitting(true);
      if (isEdit && editingId) {
        await AxiosInstance().put(`/providers/me/managers/${editingId}`, {
          firstName: trimmedFirst,
          lastName: trimmedLast,
          email: trimmedEmail,
          status: accountStatus,
        });
        setManagers((prev) =>
          prev.map((m) =>
            m.id === editingId
              ? {
                  ...m,
                  firstName: trimmedFirst,
                  lastName: trimmedLast,
                  name: [trimmedFirst, trimmedLast].filter(Boolean).join(" "),
                  email: trimmedEmail,
                  status: accountStatus,
                  enabled: accountStatus === "active",
                }
              : m,
          ),
        );
        resolveFn("Manager updated.");
      } else {
        await AxiosInstance().post("/providers/me/managers", {
          firstName: trimmedFirst,
          lastName: trimmedLast,
          email: trimmedEmail,
          ...buildPermissionsPayload(createPermDraft),
        });
        resolveFn("Manager added.");
      }
      setListVersion((v) => v + 1);
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      rejectFn(error);
    } finally {
      setSubmitting(false);
    }
  };

  const savePermissionsOnly = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!permTarget) return;
    const { pr, resolveFn, rejectFn } = getPromise();
    getPromiseToast(pr);
    try {
      setPermSaving(true);
      const body = buildPermissionsPayload(permOnlyDraft);
      await AxiosInstance().put(
        `/providers/me/managers/${permTarget.id}/permissions`,
        body,
      );
      setManagers((prev) =>
        prev.map((m) =>
          m.id === permTarget.id
            ? { ...m, permissions: { ...m.permissions, ...body.permissions } }
            : m,
        ),
      );
      resolveFn("Permissions updated.");
      setPermDialogOpen(false);
      setPermTarget(null);
    } catch (error) {
      rejectFn(error);
    } finally {
      setPermSaving(false);
    }
  };

  const setFiltersSafe = useCallback((next: any) => {
    if (typeof next === "function") {
      setFilters((prev) => next(prev));
    } else {
      setFilters(next ?? {});
    }
  }, []);

  const managerMoreActions = useMemo(
    () => [
      {
        label: "Change permissions",
        icon: <KeyRound className="h-4 w-4" />,
        onClick: (row: any) => {
          const m = rowToManager(row);
          if (m) openPermissionsOnly(m);
        },
        isShow: () => true,
      },
      {
        label: "Change password",
        icon: <Lock className="h-4 w-4" />,
        onClick: (row: any) => {
          const m = rowToManager(row);
          if (m) openChangePassword(m);
        },
        isShow: () => true,
      },
      {
        isShow: () => false,
        preventClose: true,
        render: (row: any) => {
          const m = rowToManager(row);
          if (!m) return null;
          return (
            <div
              className="flex w-full items-center justify-between gap-3 py-0.5"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                <ToggleRight className="h-4 w-4 shrink-0" aria-hidden />
                Enable
              </span>
              <Switch
                checked={m.enabled !== false}
                onCheckedChange={(checked) =>
                  void toggleManagerEnabled(m, checked)
                }
                aria-label={
                  m.enabled !== false ? `Disable ${m.name}` : `Enable ${m.name}`
                }
              />
            </div>
          );
        },
      },
    ],
    [toggleManagerEnabled],
  );

  return (
    <DashboardLayout
      activePage="/providers/me/managers"
      rightcomponent={
        <Button type="button" onClick={openAdd}>
          <Plus className="h-4 w-4" />
          Manager
        </Button>
      }
    >
      <TableWithSearch
        columnPreferenceKey="provider-manager-list"
        preferenceTableName="provider-managers"
        apiDataKey="users"
        url="/providers/me/managers"
        universalLoadingSkeleton
        fetchCondition
        isIgnoreSearchParams
        data={tableRows}
        setData={handleManagersFromApi}
        viewData={tableRows}
        columnHeaders={providerManagerTableHeaders}
        heading="Managers"
        isIncludeSearch
        searchPlaceholder="Search managers…"
        isActionColumn
        handleAction={(row: any) => {
          const m = rowToManager(row);
          if (m) openEdit(m);
        }}
        moreActions={managerMoreActions}
        clickRow={() => {}}
        filtersOptions={[]}
        filters={filters}
        setFilters={setFiltersSafe}
        defaultFilters={{}}
        isColumnSelection
        params={{ listVersion }}
      />

      <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="flex max-h-[min(85dvh,720px)] flex-col overflow-hidden sm:max-w-md sm:gap-0">
          <form
            onSubmit={handleSubmit}
            className="flex min-h-0 flex-1 flex-col overflow-hidden"
          >
            <DialogHeader className="shrink-0 space-y-2 text-left">
              <DialogTitle>{isEdit ? "Edit manager" : "Add manager"}</DialogTitle>
              <DialogDescription className="sr-only">
                {isEdit ? "Update manager details" : "Create a provider manager"}
              </DialogDescription>
            </DialogHeader>
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain py-1 px-1">
              <div className="space-y-2">
                <Label htmlFor="pm-first">First name</Label>
                <Input
                  id="pm-first"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoComplete="given-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pm-last">Last name</Label>
                <Input
                  id="pm-last"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pm-email">Email</Label>
                <Input
                  id="pm-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>
              {isEdit &&
                (accountStatus !== "inactive" ? (
                  <div className="space-y-2">
                    <Label htmlFor="pm-status">Status</Label>
                    <Select
                      value={accountStatus}
                      onValueChange={(v) =>
                        setAccountStatus(v as ProviderAccountStatus)
                      }
                    >
                      <SelectTrigger id="pm-status" className="w-full">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent className="z-[300]">
                        {PROVIDER_ACCOUNT_STATUS_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="pm-status">Status</Label>
                    <Input id="pm-status" value="Inactive" disabled />
                  </div>
                ))}
              {!isEdit && (
                <div className="space-y-3">
                  <Label>Access permissions</Label>
                  {catalogLoading ? (
                    <p className="text-sm text-muted-foreground px-1 py-2">
                      Loading permissions…
                    </p>
                  ) : catalog.length === 0 ? (
                    <p className="text-sm text-muted-foreground px-1 py-2">
                      No permissions available from catalog.
                    </p>
                  ) : (
                    <AccessNavPermissionFields
                      items={catalog}
                      draft={createPermDraft}
                      onToggle={(key, checked) =>
                        setCreatePermDraft((p) => ({ ...p, [key]: checked }))
                      }
                      idPrefix="pm-create"
                    />
                  )}
                </div>
              )}
            </div>
            <DialogFooter className="mt-0 shrink-0 gap-2 border-t bg-background pt-4 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleDialogOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting
                  ? isEdit
                    ? "Saving…"
                    : "Adding…"
                  : isEdit
                    ? "Save changes"
                    : "Add manager"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={permDialogOpen}
        onOpenChange={(open) => {
          setPermDialogOpen(open);
          if (!open) {
            setPermTarget(null);
            permDraftSyncedForUserIdRef.current = null;
          }
        }}
      >
        <DialogContent className="flex max-h-[min(85dvh,720px)] flex-col overflow-hidden sm:max-w-md sm:gap-0">
          <form onSubmit={savePermissionsOnly} className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <DialogHeader className="shrink-0 space-y-2 text-left">
              <DialogTitle>Change permissions</DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                {permTarget
                  ? `Navigation access for ${permTarget.name}`
                  : "Select permissions"}
              </DialogDescription>
            </DialogHeader>
            <div className="min-h-0 flex-1 overflow-y-auto py-2">
              {catalogLoading ? (
                <p className="text-sm text-muted-foreground px-1 py-2">
                  Loading permissions…
                </p>
              ) : catalog.length === 0 ? (
                <p className="text-sm text-muted-foreground px-1 py-2">
                  No permissions available from catalog.
                </p>
              ) : (
                <AccessNavPermissionFields
                  items={catalog}
                  draft={permOnlyDraft}
                  onToggle={(key, checked) =>
                    setPermOnlyDraft((p) => ({ ...p, [key]: checked }))
                  }
                  idPrefix="pm-perm"
                />
              )}
            </div>
            <DialogFooter className="mt-0 shrink-0 gap-2 border-t bg-background pt-4 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                disabled={permSaving}
                onClick={() => {
                  setPermDialogOpen(false);
                  setPermTarget(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={permSaving || catalogLoading || catalog.length === 0}
              >
                {permSaving ? "Saving…" : "Save permissions"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={passwordDialogOpen}
        onOpenChange={(open) => {
          setPasswordDialogOpen(open);
          if (!open) setPasswordTarget(null);
        }}
      >
        <DialogContent className="max-h-[85dvh] overflow-y-auto sm:max-w-md sm:gap-4">
          <DialogHeader className="space-y-2 text-left">
            <DialogTitle>Change password</DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              {passwordTarget
                ? `Set a new password for ${passwordTarget.name} (${passwordTarget.email}).`
                : "Select a manager from the table."}
            </DialogDescription>
          </DialogHeader>
          {passwordTarget ? (
            <RHFWrapper
              key={passwordTarget.id}
              initialValues={{ password: "", confirmPassword: "" }}
              isValidate
              validateSchema={ResetSchema}
              className="space-y-4"
              onSubmit={(data) => void handleProviderResetPassword(data)}
            >
              <RhfInput
                name="password"
                label="New password"
                type="password"
                placeholder="New password"
                autoComplete="new-password"
              />
              <RhfInput
                name="confirmPassword"
                label="Confirm password"
                type="password"
                placeholder="Confirm password"
                autoComplete="new-password"
              />
              <DialogFooter className="gap-2 border-t pt-4 sm:justify-end sm:gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={passwordSaving}
                  onClick={() => {
                    setPasswordDialogOpen(false);
                    setPasswordTarget(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={passwordSaving}>
                  {passwordSaving ? "Saving…" : "Update password"}
                </Button>
              </DialogFooter>
            </RHFWrapper>
          ) : null}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
